import React, { useState, useEffect, useImperativeHandle } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { textEllipsis } from '@ecp/common/src/style/theme/defaultStyled';
import { FlexBox, FlexBoxColumn, FlexBoxSpaceBetween } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';

import { menusState } from '@bo-recoil/common/atom';
import { withMenu } from '@bo-recoil/common/selector';
import LnbLeafMenuGroup from './LnbLeafMenuGroup';
import { ReactComponent as PlusIcon } from '@bo-assets/icon/common/icon__lnb-plus.svg';
import { ReactComponent as MinusIcon } from '@bo-assets/icon/common/icon__lnb-minus.svg';

const Container = styled(FlexBoxColumn)`
  width: 100%;
  cursor: pointer;
`;

const MenuHeaderWrapper = styled(FlexBox)`
  width: 100%;
  height: 42px;
  align-items: stretch;
`;

const GroupNameWrapper = styled(FlexBoxSpaceBetween)`
  width: 232px;
  border-bottom: 1px solid #595877;
  box-sizing: border-box;
`;

const GroupName = styled.div`
  font-size: ${boTheme.font.size.group};
  font-weight: ${boTheme.font.weight.bold};
  color: ${boTheme.color.sub10};
  ${textEllipsis}
`;

const LnbMenuGroup = React.forwardRef(({ menuGroupNumber }, ref) => {
  const menuGroup = useRecoilValue(withMenu(menuGroupNumber));
  const { menus } = useRecoilValue(menusState);
  const [open, setOpen] = useState(false);

  const { pathname } = useLocation();

  useImperativeHandle(ref, () => ({
    close: () => {
      setOpen(false);
    },
  }));

  useEffect(() => {
    setOpen(
      (prev) =>
        prev ||
        menuGroup?.menus.some((leafMenuGroupNumber) =>
          menus[leafMenuGroupNumber].menus.some(
            (leafMenuNumber) =>
              menus[leafMenuNumber].menuUrl?.trim?.()?.length > 0 &&
              (pathname?.endsWith(menus[leafMenuNumber].menuUrl) ||
                pathname?.includes(`${menus[leafMenuNumber].menuUrl}/`))
          )
        )
    );
  }, [pathname, menuGroup, menus]);
  return (
    <Container>
      <MenuHeaderWrapper onClick={() => setOpen((prev) => !prev)}>
        <Spacing left={24} />
        <GroupNameWrapper>
          <GroupName>{menuGroup.menuName || ''}</GroupName>
          {open ? <MinusIcon /> : <PlusIcon />}
        </GroupNameWrapper>
      </MenuHeaderWrapper>
      {open &&
        menuGroup.menus?.map((leafMenuGroupNumber) => (
          <LnbLeafMenuGroup key={leafMenuGroupNumber} leafMenuGroupNumber={leafMenuGroupNumber} />
        ))}
    </Container>
  );
});

LnbMenuGroup.propTypes = {
  menuGroupNumber: PropTypes.string,
};

export default LnbMenuGroup;

LnbMenuGroup.displayName = 'LnbMenuGroup';
